import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput, PasswordStrength } from "@/components/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CircleNotch, Envelope, LockKey, User, Gift, CheckCircle, WarningCircle,
  Phone, Briefcase, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1631812314418-d2c27f4d85ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxhbWFsZmklMjBjb2FzdCUyMHN1bnNldHxlbnwwfHx8fDE3NzY5ODg2NTR8MA&ixlib=rb-4.1.0&q=85";

// Loose phone regex matching backend rule (digits + optional + prefix, ignoring formatting chars)
const PHONE_RE = /^\+?\d{7,20}$/;
function isValidPhone(v) {
  if (!v) return false;
  const cleaned = v.replace(/[\s\-().]/g, "");
  return PHONE_RE.test(cleaned);
}

export default function Register() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [availability, setAvailability] = useState("");
  const [startTimeline, setStartTimeline] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [loading, setLoading] = useState(false);

  // referral validation: null=not checked, "checking", {valid, name}, {invalid}
  const [refState, setRefState] = useState(null);

  useEffect(() => {
    const ref = (params.get("ref") || "").trim();
    if (ref) setReferralCode(ref.toUpperCase());
  }, [params]);

  // Live-validate referral code (debounced)
  useEffect(() => {
    const code = referralCode.trim();
    if (!code) { setRefState(null); return; }
    if (code.length < 4) { setRefState(null); return; }
    let cancelled = false;
    setRefState({ checking: true });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/referral-info`, { params: { code } });
        if (!cancelled) setRefState({ valid: true, agentName: data.referring_agent_name });
      } catch {
        if (!cancelled) setRefState({ valid: false });
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [referralCode]);

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  }

  const phoneValid = isValidPhone(phone);
  const phoneError = phoneTouched && !phoneValid;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!phoneValid) {
      setPhoneTouched(true);
      toast.error("Please enter a valid phone number");
      return;
    }
    if (referralCode && refState && refState.valid === false) {
      toast.error("Referral code is invalid");
      return;
    }
    setLoading(true);
    const res = await register(email, password, name, referralCode, {
      phone,
      availability,
      start_timeline: startTimeline,
    });
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to Best Time Travel & Tours");
      navigate("/dashboard");
    } else {
      toast.error(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-ink text-white grain">
      <div className="absolute inset-0">
        <img src={BG_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/90" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="p-6 sm:p-8">
          <Logo size="default" />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-4 font-sans">
                Become an Agent
              </p>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-white mb-3">
                Join the <span className="italic text-[#D4AF37]">studio</span>
              </h1>
              <p className="text-white/60 text-sm font-sans">
                Create your agent account to start crafting unforgettable journeys.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form" noValidate>
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Full name
                </Label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    className="pl-10 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Email
                </Label>
                <div className="relative">
                  <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    className="pl-10 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Phone number
                </Label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                  <Input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={() => setPhoneTouched(true)}
                    required
                    autoComplete="tel"
                    placeholder="+1 (555) 123-4567"
                    className={`pl-10 h-12 bg-black/40 text-white font-sans focus:ring-[#D4AF37]/20 ${
                      phoneError ? "border-red-400/60 focus:border-red-400" : "border-white/10 focus:border-[#D4AF37]"
                    }`}
                    data-testid="register-phone-input"
                    aria-invalid={phoneError ? "true" : "false"}
                  />
                </div>
                {phoneError && (
                  <p className="text-[11px] text-red-300 mt-1.5 font-sans flex items-center gap-1.5" data-testid="register-phone-error">
                    <WarningCircle size={14} weight="duotone" /> Enter a valid phone number (digits, optional + prefix)
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Password
                </Label>
                <PasswordInput
                  icon={LockKey}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="register-password-input"
                />
                <PasswordStrength value={password} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="availability" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                    Availability <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Select value={availability} onValueChange={setAvailability}>
                    <SelectTrigger
                      id="availability"
                      className="h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                      data-testid="register-availability-select"
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase size={16} weight="duotone" className="text-white/40" />
                        <SelectValue placeholder="Choose…" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      <SelectItem value="full_time">Full-time</SelectItem>
                      <SelectItem value="part_time">Part-time</SelectItem>
                      <SelectItem value="flexible">Flexible / Side Hustle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="start-timeline" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                    Start timeline <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Select value={startTimeline} onValueChange={setStartTimeline}>
                    <SelectTrigger
                      id="start-timeline"
                      className="h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
                      data-testid="register-timeline-select"
                    >
                      <div className="flex items-center gap-2">
                        <Clock size={16} weight="duotone" className="text-white/40" />
                        <SelectValue placeholder="Choose…" />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      <SelectItem value="immediate">Immediately</SelectItem>
                      <SelectItem value="1_2_weeks">Within 1–2 weeks</SelectItem>
                      <SelectItem value="exploring">Just exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="referral" className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                  Referral code <span className="text-white/30 normal-case tracking-normal">(optional)</span>
                </Label>
                <div className="relative">
                  <Gift size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
                  <Input
                    id="referral"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="e.g. AB7K9XYZ"
                    autoComplete="off"
                    maxLength={12}
                    className="pl-10 h-12 bg-black/40 border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans uppercase tracking-widest"
                    data-testid="register-referral-input"
                  />
                </div>
                {refState?.checking && (
                  <p className="text-[11px] text-white/40 mt-1.5 font-sans flex items-center gap-1.5" data-testid="referral-checking">
                    <CircleNotch size={12} className="animate-spin" /> Checking referral code…
                  </p>
                )}
                {refState?.valid === true && (
                  <p className="text-[11px] text-emerald-300 mt-1.5 font-sans flex items-center gap-1.5" data-testid="referral-valid">
                    <CheckCircle size={14} weight="duotone" /> Referred by <strong className="text-emerald-200">{refState.agentName}</strong>
                  </p>
                )}
                {refState?.valid === false && (
                  <p className="text-[11px] text-red-300 mt-1.5 font-sans flex items-center gap-1.5" data-testid="referral-invalid">
                    <WarningCircle size={14} weight="duotone" /> Referral code not recognized
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || (referralCode && refState?.valid === false)}
                className="w-full h-12 bg-[#D4AF37] hover:bg-[#F3C853] text-black font-medium text-sm uppercase tracking-[0.25em] transition-all duration-200 hover:-translate-y-[1px]"
                data-testid="register-submit-btn"
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Create Account"}
              </Button>

              <div className="text-center pt-1">
                <Link
                  to="/login"
                  data-testid="register-login-link"
                  className="inline-block text-[11px] uppercase tracking-[0.3em] text-white/50 hover:text-[#D4AF37] transition-colors font-sans"
                >
                  Already have an account? Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="p-6 text-center text-[11px] uppercase tracking-[0.3em] text-white/30 font-sans">
          A curated concierge experience
        </div>
      </div>
    </div>
  );
}
